import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Package, MapPin, Camera, PencilSimple, Check, X, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminAssets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addressFilter, setAddressFilter] = useState(searchParams.get("address_key") || "");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(searchParams.get("open") || null);
  const [uploadBusy, setUploadBusy] = useState({}); // asset_id -> bool
  const [detailBusy, setDetailBusy] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [rescanBusy, setRescanBusy] = useState(false);
  const [rescanResult, setRescanResult] = useState(null);

  const doRescanPunchPhotos = async (dryRun) => {
    const addr = assets.find(a => a.address_key === addressFilter)?.address;
    if (!addr) { toast.error("Pick an address first"); return; }
    setRescanBusy(true);

    // Dry run — one call, no chunking needed
    if (dryRun) {
      try {
        const { data } = await api.post(
          `/admin/rescan-punch-photos-for-appliances`,
          null,
          { params: { address: addr, dry_run: true, limit: 2000 }, timeout: 30000 }
        );
        setRescanResult(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed"));
      } finally { setRescanBusy(false); }
      return;
    }

    // Live run — chunk-loop to dodge Cloudflare's 100s edge timeout.
    // Endpoint is idempotent; each chunk processes 25 unscanned photos
    // (~10-15s Gemini) then returns. We keep looping until nothing new
    // was scanned.
    const CHUNK = 25;
    const totals = {
      photos_scanned: 0, nameplates_found: 0, assets_inserted: 0,
      assets_photo_attached: 0, assets_skipped_dupe: 0, fetch_errors: 0,
      photos_considered: 0, already_rescanned: 0,
    };
    const unitCounts = {};
    const tid = toast.loading("Scanning punch photos with AI Vision (Gemini)…");
    let chunks = 0;
    let stopReason = "done";

    try {
      while (chunks < 150) {  // 150 × 12 = 1800 photo hard ceiling
        chunks += 1;
        let data;
        try {
          const r = await api.post(
            `/admin/rescan-punch-photos-for-appliances`,
            null,
            { params: { address: addr, dry_run: false, limit: CHUNK, concurrency: 4 },
              timeout: 90000 }
          );
          data = r.data;
        } catch (err) {
          // Cloudflare 524 / network blip → keep going, endpoint is idempotent
          if (chunks < 150) {
            toast.loading(
              `AI Vision — chunk ${chunks} retrying… (${totals.photos_scanned} scanned so far)`,
              { id: tid });
            await new Promise(r => setTimeout(r, 2000));
            continue;
          }
          throw err;
        }

        totals.photos_considered = data.photos_considered;
        totals.already_rescanned = Math.max(totals.already_rescanned, data.already_rescanned || 0);
        totals.photos_scanned += data.photos_scanned || 0;
        totals.nameplates_found += data.nameplates_found || 0;
        totals.assets_inserted += data.assets_inserted || 0;
        totals.assets_photo_attached += data.assets_photo_attached || 0;
        totals.assets_skipped_dupe += data.assets_skipped_dupe || 0;
        totals.fetch_errors += data.fetch_errors || 0;
        (data.top_units || []).forEach(u => {
          unitCounts[u.unit] = (unitCounts[u.unit] || 0) + u.count;
        });

        setRescanResult({
          ...totals,
          top_units: Object.entries(unitCounts).sort((a, b) => b[1] - a[1])
            .slice(0, 10).map(([u, c]) => ({ unit: u, count: c })),
          _live: true,
        });

        const done = data.photos_considered || 0;
        const scanned = (totals.already_rescanned + totals.photos_scanned);
        toast.loading(
          `AI Vision — ${scanned}/${done} photos · ${totals.assets_inserted} new appliances`,
          { id: tid });

        // Nothing scanned this chunk = all done (or only string photos left)
        if ((data.photos_scanned || 0) === 0) {
          stopReason = "done";
          break;
        }
      }

      toast.success(
        `Rescan complete · ${totals.photos_scanned} photos scanned this session · ${totals.assets_inserted} new appliances · ${totals.assets_photo_attached} photos attached to existing`,
        { id: tid, duration: 10000 }
      );
      load();
    } catch (e) {
      toast.error(
        `Rescan interrupted (${totals.photos_scanned} scanned, ${totals.assets_inserted} inserted). Click Rescan again to resume.`,
        { id: tid, duration: 8000 }
      );
    } finally { setRescanBusy(false); }
  };

  const doSyncPreview = async () => {
    setSyncBusy(true);
    try {
      const { data } = await api.post("/admin/backfill-walk-assets?dry_run=true");
      setSyncPreview(data);
      // If a specific address filter is active, ALSO fetch the diagnostic so
      // Andrew can see where walks are hiding when the sync count is low.
      if (addressFilter) {
        const addr = assets.find(a => a.address_key === addressFilter)?.address;
        if (addr) {
          try {
            const dg = await api.get(`/admin/asset-sources-audit?address=${encodeURIComponent(addr)}`);
            setSyncPreview(p => ({ ...(p || data), diagnostic: dg.data }));
          } catch {}
        }
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed"));
    } finally { setSyncBusy(false); }
  };

  const doSyncRun = async () => {
    setSyncBusy(true);
    const t = toast.loading("Syncing walk-mode assets…");
    try {
      const { data } = await api.post("/admin/backfill-walk-assets?dry_run=false");
      toast.success(`Synced ${data.inserted} new assets · ${data.skipped_existing} already synced`, { id: t });
      setSyncOpen(false);
      setSyncPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sync failed"), { id: t });
    } finally { setSyncBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/assets${addressFilter ? `?address_key=${encodeURIComponent(addressFilter)}` : ""}`);
      setAssets(r.data.assets || []);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [addressFilter]);

  const addresses = Array.from(new Set(assets.map(a => a.address_key).filter(Boolean))).sort();
  // Extract units from either asset.unit (walk-mode source) or the "1A ", "12C "
  // prefix on asset.location (legacy AdminAssets naming convention).
  const unitOfAsset = (a) => {
    if (a.unit) return String(a.unit).trim();
    const m = /^\s*(\d{1,2}\s?[A-Ea-e])/.exec(a.location || "");
    return m ? m[1].replace(/\s+/g, "").toUpperCase() : "";
  };
  const [unitFilter, setUnitFilter] = useState("");
  const units = Array.from(new Set(assets.map(unitOfAsset).filter(Boolean))).sort();
  const filtered = assets.filter(a => {
    if (unitFilter && unitOfAsset(a) !== unitFilter) return false;
    if (!q) return true;
    const n = q.toLowerCase();
    return (a.name || "").toLowerCase().includes(n)
      || (a.location || "").toLowerCase().includes(n)
      || (a.model || "").toLowerCase().includes(n)
      || (a.serial || "").toLowerCase().includes(n)
      || (a.address || "").toLowerCase().includes(n);
  });

  const handleBatchUpload = async (asset, evt) => {
    const files = Array.from(evt.target.files || []);
    evt.target.value = "";
    if (!files.length) return;
    setUploadBusy(b => ({ ...b, [asset.id]: true }));
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const { data } = await api.post(`/assets/${asset.id}/photos-batch`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const merged = Object.keys(data.merged_fields || {});
      toast.success(`${data.uploaded} photo(s) attached${merged.length ? ` · auto-filled: ${merged.join(", ")}` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"));
    } finally {
      setUploadBusy(b => ({ ...b, [asset.id]: false }));
    }
  };

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="admin-assets-page">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Package weight="fill" size={28} className="text-fuchsia-600"/>
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Assets</h1>
          </div>
          <button onClick={() => { setSyncOpen(true); setSyncPreview(null); doSyncPreview(); }}
            data-testid="sync-walk-assets-btn"
            className="tactile-btn h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
            <ArrowsClockwise weight="bold" size={13}/> Sync Walk-Mode Assets
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-600 font-medium">
          Washers, dryers, HVAC, water heaters — everything you're tracking. Tap <b>Attach Photos</b> on any card to batch-upload nameplate pics from your phone gallery. Claude auto-fills model &amp; serial.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_180px] gap-2">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / location / model…"
            data-testid="asset-search-input"
            className="h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
          <select value={addressFilter} onChange={e => { setAddressFilter(e.target.value); setUnitFilter(""); }}
            data-testid="asset-address-filter"
            className="h-11 px-2 border-2 border-[#0f172a] font-bold text-sm">
            <option value="">All addresses ({assets.length})</option>
            {addresses.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)}
            data-testid="asset-unit-filter"
            disabled={units.length === 0}
            className="h-11 px-2 border-2 border-[#0f172a] font-bold text-sm disabled:opacity-50">
            <option value="">All units ({units.length || "—"})</option>
            {units.map(u => <option key={u} value={u}>Unit {u}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="mt-6 font-black uppercase text-xs tracking-widest text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-6 rugged-card p-8 text-center bg-white" data-testid="assets-empty">
            <Package weight="bold" size={40} className="mx-auto text-slate-400"/>
            <div className="mt-2 font-black uppercase text-sm tracking-wider text-slate-500">
              {q || addressFilter ? "No matches" : "No assets yet"}
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map(a => {
              const busy = uploadBusy[a.id];
              return (
                <div key={a.id} onClick={(e) => { if (e.target.tagName !== "INPUT" && e.target.tagName !== "LABEL") setOpenId(a.id); }}
                  className={`p-3 bg-white border-2 cursor-pointer hover:border-fuchsia-500 ${a.is_large_load ? "border-purple-500" : "border-slate-300"}`}
                  data-testid={`asset-card-${a.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm truncate">{a.name}</div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5 truncate">
                        <MapPin size={10} className="inline"/> {a.address}{a.location && ` · ${a.location}`}
                      </div>
                      {(a.make || a.model || a.serial) && (
                        <div className="mt-1 text-[10px] text-slate-600 truncate">
                          {a.make && <span className="mr-2"><b>Make:</b> {a.make}</span>}
                          {a.model && <span className="mr-2"><b>Model:</b> {a.model}</span>}
                          {a.serial && <span><b>SN:</b> {a.serial}</span>}
                        </div>
                      )}
                      {(a.photos || []).length > 0 && (
                        <div className="mt-2 flex gap-1 overflow-x-auto pb-1">
                          {a.photos.map((p, i) => (
                            <img key={i} src={`${BASE}/api/files/${encodeURIComponent(p.path)}?auth=${encodeURIComponent(localStorage.getItem("access_token") || localStorage.getItem("token") || "")}`}
                              alt="" data-testid={`asset-photo-${a.id}-${i}`}
                              className="h-16 w-16 object-cover border-2 border-[#0f172a] flex-shrink-0"/>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${a.status === "operational" ? "bg-emerald-600 text-white" : a.status === "broken" ? "bg-red-700 text-white" : "bg-slate-400 text-white"}`}>
                      {a.status}
                    </span>
                  </div>

                  {a.warranty_status === "active" && (
                    <div className="mt-2 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-700"
                      data-testid={`asset-warranty-chip-${a.id}`}>
                      🛡️ Warranty · {a.warranty_days_left}d left
                      {a.warranty_provider && <span className="text-slate-500 ml-1 normal-case tracking-normal">· {a.warranty_provider}</span>}
                    </div>
                  )}
                  {a.warranty_status === "expiring_soon" && (
                    <div className="mt-2 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-700"
                      data-testid={`asset-warranty-chip-${a.id}`}>
                      ⏳ Warranty expiring · {a.warranty_days_left}d
                    </div>
                  )}
                  {a.replace_recommendation?.flag && (
                    <div data-testid={`asset-replace-flag-${a.id}`}
                      className={`mt-2 p-2 border-l-4 ${a.replace_recommendation.under_warranty ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-red-500 bg-red-50 text-red-800"}`}>
                      <div className="text-[9px] font-black uppercase tracking-widest">
                        {a.replace_recommendation.under_warranty ? "🛡️ Under Warranty · Claim Replacement" : "⚠︎ Replace, Don't Repair"}
                      </div>
                      <div className="text-[10px] mt-0.5">{a.replace_recommendation.reason}</div>
                    </div>
                  )}

                  <label className={`mt-3 h-10 flex items-center justify-center gap-1 text-[11px] font-black uppercase tracking-widest cursor-pointer ${busy ? "bg-slate-300 text-slate-500 cursor-wait" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white"}`}>
                    <Camera weight="fill" size={14}/> {busy ? "OCR running…" : ((a.photos || []).length > 0 ? "Add More Photos" : "Attach Photos + AI OCR")}
                    <input type="file" accept="image/*" multiple onChange={e => handleBatchUpload(a, e)}
                      disabled={busy} data-testid={`asset-upload-${a.id}`} className="hidden"/>
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {openId && (
        <AssetDetailModal
          asset={assets.find(a => a.id === openId)}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
      {syncOpen && (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-end sm:items-center justify-center p-3"
             onClick={() => !syncBusy && setSyncOpen(false)}>
          <div className="bg-white w-full max-w-md border-4 border-emerald-500 shadow-[6px_6px_0_#0f172a]"
               onClick={e => e.stopPropagation()} data-testid="sync-walk-modal">
            <div className="bg-emerald-600 text-white p-4">
              <div className="text-[10px] font-black uppercase tracking-[.2em] opacity-70">One-tap migration</div>
              <h2 className="text-xl font-black mt-1">Sync Walk-Mode Assets</h2>
              <p className="text-xs opacity-90 mt-1">
                Copies every historical nameplate scan from walk-mode into the Assets registry.
                Safe to run — idempotent, won't duplicate.
              </p>
            </div>
            <div className="p-5">
              {syncBusy && !syncPreview && (
                <div className="text-slate-500 font-bold text-sm">Loading preview…</div>
              )}
              {syncPreview && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 bg-slate-50 border-2 border-slate-200">
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Scanned</div>
                      <div className="text-xl font-black" data-testid="sync-scanned">{syncPreview.scanned}</div>
                    </div>
                    <div className="p-3 bg-emerald-50 border-2 border-emerald-500">
                      <div className="text-[9px] font-black uppercase tracking-widest text-emerald-800">To Insert</div>
                      <div className="text-xl font-black text-emerald-700" data-testid="sync-inserted">{syncPreview.inserted}</div>
                    </div>
                    <div className="p-3 bg-slate-100 border-2 border-slate-300">
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-600">Already Synced</div>
                      <div className="text-xl font-black text-slate-700">{syncPreview.skipped_existing}</div>
                    </div>
                  </div>
                  {syncPreview.top_addresses?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Top buildings to fix</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {syncPreview.top_addresses.map((a, i) => (
                          <div key={i} className="flex justify-between text-xs px-2 py-1 bg-slate-50 border border-slate-200">
                            <span className="font-bold truncate">{a.address}</span>
                            <span className="font-black text-emerald-700">+{a.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {syncPreview.inserted === 0 && !syncPreview.diagnostic && (
                    <div className="p-3 bg-cyan-50 border-2 border-cyan-500 text-xs font-medium">
                      Nothing to sync — every walk-mode asset is already in the registry. 🎉
                    </div>
                  )}
                  {syncPreview.diagnostic && (
                    <div className="p-3 bg-amber-50 border-2 border-amber-500 text-xs" data-testid="sync-diagnostic">
                      <div className="font-black uppercase tracking-widest text-amber-900 mb-2">
                        📊 Diagnostic for {syncPreview.diagnostic.address}
                      </div>
                      <div className="space-y-1">
                        <div>Walk-OCR assets: <b>{syncPreview.diagnostic.sources.property_configs_assets.units_with_assets} units</b> · {syncPreview.diagnostic.sources.property_configs_assets.total_assets} assets</div>
                        <div>db.assets registry: <b>{syncPreview.diagnostic.sources.db_assets.count}</b> rows</div>
                        <div>walk_reports (raw): <b>{syncPreview.diagnostic.sources.walk_reports.reports}</b> reports · {syncPreview.diagnostic.sources.walk_reports.total_photos} photos</div>
                        <div>ProjectRadar projects: <b>{syncPreview.diagnostic.sources.projects_radar.projects}</b> · <b>{syncPreview.diagnostic.sources.projects_radar.project_photos}</b> punch photos</div>
                        <div>Voice-walk jobs: <b>{syncPreview.diagnostic.sources.jobs_voice_walk_assets.jobs_with_assets}</b></div>
                      </div>
                      {syncPreview.diagnostic.sources.projects_radar.project_photos > 0 && (
                        <div className="mt-3 pt-3 border-t-2 border-amber-300">
                          <div className="font-black uppercase tracking-widest text-fuchsia-800 text-[10px] mb-1">
                            💡 {syncPreview.diagnostic.sources.projects_radar.project_photos} punch photos found
                          </div>
                          <div className="text-[11px] mb-2">
                            These are walkthrough photos never OCR'd for appliances. Rescan with <b>AI Vision (Gemini 3 Flash)</b> — ~$0.0002/photo, runs in ~30s chunks. Safe to re-run; already-scanned photos are skipped automatically.
                          </div>
                          {rescanResult && (
                            <div className="mb-2 p-2 bg-white border-2 border-fuchsia-300 space-y-0.5 text-[11px]" data-testid="rescan-result">
                              <div className="font-black uppercase tracking-widest text-[9px] text-fuchsia-700">Last rescan</div>
                              <div>Photos considered: <b>{rescanResult.photos_considered}</b> · already scanned: <b>{rescanResult.already_rescanned}</b></div>
                              {rescanResult.would_scan != null && <div>Would scan: <b>{rescanResult.would_scan}</b></div>}
                              {rescanResult.photos_scanned > 0 && (
                                <>
                                  <div>Photos scanned this run: <b>{rescanResult.photos_scanned}</b></div>
                                  <div className="text-emerald-700">Nameplates found: <b>{rescanResult.nameplates_found}</b></div>
                                  <div className="text-emerald-700">New assets inserted: <b>{rescanResult.assets_inserted}</b></div>
                                  <div>Photos attached to existing: <b>{rescanResult.assets_photo_attached}</b></div>
                                  {rescanResult.top_units?.length > 0 && (
                                    <div>Top units: {rescanResult.top_units.slice(0, 5).map(u => `${u.unit}(+${u.count})`).join(', ')}</div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button onClick={() => doRescanPunchPhotos(true)}
                              disabled={rescanBusy}
                              data-testid="rescan-punch-preview-btn"
                              className="flex-1 h-9 px-2 border-2 border-fuchsia-500 bg-white text-fuchsia-700 font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
                              {rescanBusy ? "…" : "Preview"}
                            </button>
                            <button onClick={() => doRescanPunchPhotos(false)}
                              disabled={rescanBusy}
                              data-testid="rescan-punch-run-btn"
                              className="flex-1 h-9 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50 shadow-[2px_2px_0_#0f172a] inline-flex items-center justify-center gap-1">
                              <Sparkle weight="fill" size={11}/> {rescanBusy ? "Scanning…" : "Rescan with AI"}
                            </button>
                          </div>
                        </div>
                      )}
                      {syncPreview.diagnostic.sources.walk_reports.unit_breakdown?.some(u => u.photos > 0 && u.nameplates === 0) && (
                        <div className="mt-2 pt-2 border-t-2 border-amber-300">
                          ⚠️ <b>Some walks have photos but 0 OCR'd nameplates</b> — the tech uploaded photos but the vision model didn't classify them as nameplates. Re-open the walk report and use "Scan Nameplate" mode explicitly.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 border-t-2 border-slate-200 flex gap-2 bg-slate-50">
              <button onClick={() => setSyncOpen(false)} disabled={syncBusy}
                className="flex-1 h-11 border-2 border-slate-400 bg-white font-black uppercase text-xs tracking-widest disabled:opacity-60">
                Cancel
              </button>
              <button onClick={doSyncRun}
                disabled={syncBusy || !syncPreview || syncPreview.inserted === 0}
                data-testid="sync-run-btn"
                className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-40 shadow-[3px_3px_0_#0f172a]">
                <ArrowsClockwise weight="bold" size={14}/>
                {syncBusy ? "Syncing…" : `Sync ${syncPreview?.inserted || 0} Assets`}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function AssetDetailModal({ asset, onClose, onChanged }) {
  const [draft, setDraft] = useState(asset);
  const [saving, setSaving] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const pressTimer = useRef(null);
  const startPress = (p) => { pressTimer.current = setTimeout(() => setPendingDelete(p), 600); };
  const cancelPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const confirmDelete = async () => {
    const p = pendingDelete; setPendingDelete(null);
    try {
      const { data } = await api.delete(`/assets/${asset.id}/photo`, { params: { path: p.path }});
      setDraft(data); onChanged?.(data); toast.success("Photo removed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed")); }
  };
  const printSticker = () => {
    const qrData = encodeURIComponent(`${window.location.origin}/admin/assets?open=${asset.id}`);
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=0&data=${qrData}`;
    const w = window.open("", "_blank", "width=420,height=520");
    if (!w) return;
    const esc = (s) => (s || "").toString().replace(/</g, "&lt;");
    w.document.write(`<html><head><title>QR · ${esc(asset.name || asset.id)}</title><style>body{font-family:sans-serif;padding:20px;text-align:center;margin:0}h1{font-size:14px;text-transform:uppercase;letter-spacing:2px;margin:0 0 4px}.sub{font-size:10px;color:#666;margin-bottom:12px}img{border:2px solid #000;padding:8px;background:white}.foot{margin-top:8px;font-size:9px;color:#999;letter-spacing:1px;text-transform:uppercase}@media print{.no-print{display:none}}</style></head><body><h1>${esc(asset.name || "Asset")}</h1><div class="sub">${esc(asset.address)} · ${esc(asset.location)}</div><img src="${qrUrl}" alt="QR"/><div class="foot">Scan → Open in Field Radar</div><button class="no-print" onclick="window.print()" style="margin-top:12px;padding:8px 20px;font-weight:900;background:#0f172a;color:white;border:0;text-transform:uppercase;letter-spacing:2px;font-size:10px">Print</button></body></html>`);
    w.document.close();
  };
  useEffect(() => { setDraft(asset); }, [asset]);
  if (!asset || !draft) return null;
  const photos = draft.photos || [];
  const authTok = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  const streamUrl = (p) => `${BASE}/api/files/${encodeURIComponent(p.path)}?auth=${encodeURIComponent(authTok)}`;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["name","make","model","serial","install_date","notes","status","vendor","location",
       "warranty_start","warranty_end","warranty_provider","warranty_policy_number"].forEach(k => {
        if ((draft[k] ?? "") !== (asset[k] ?? "")) payload[k] = draft[k] || "";
      });
      // warranty_years is numeric/null — normalize before diff
      const draftYears = draft.warranty_years === "" || draft.warranty_years == null ? null : Number(draft.warranty_years);
      const assetYears = asset.warranty_years == null ? null : Number(asset.warranty_years);
      if (draftYears !== assetYears) payload.warranty_years = draftYears;
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.patch(`/assets/${asset.id}`, payload);
      toast.success("Saved");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const field = (label, key, testid, placeholder = "") => (
    <div>
      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</label>
      <input value={draft[key] || ""} onChange={e => setDraft({ ...draft, [key]: e.target.value })}
        data-testid={testid} placeholder={placeholder}
        className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1"/>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase truncate mr-4">{asset.name}</div>
          <button onClick={onClose} data-testid="asset-detail-close" className="p-1 hover:bg-white/20"><X size={22}/></button>
        </div>
        <div className="p-5 space-y-4" data-testid="asset-detail-modal">
          <div className="text-xs text-slate-600"><MapPin size={12} className="inline"/> {asset.address}{asset.location && ` · ${asset.location}`}</div>

          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="asset-photo-grid">
              {photos.map((p, i) => (
                <button key={i}
                  onClick={() => setPhotoIdx(i)}
                  onPointerDown={() => startPress(p)}
                  onPointerUp={cancelPress}
                  onPointerLeave={cancelPress}
                  onContextMenu={(e) => { e.preventDefault(); setPendingDelete(p); }}
                  data-testid={`asset-detail-photo-${i}`}
                  className="w-full aspect-square block select-none touch-none">
                  <img src={streamUrl(p)} alt=""
                    className="w-full h-full object-cover border-2 border-[#0f172a] hover:border-fuchsia-500 pointer-events-none"/>
                </button>
              ))}
            </div>
          )}
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-3">
            <span>Long-press a photo to delete</span>
            <button onClick={printSticker} data-testid="asset-qr-print"
              className="ml-auto px-2 py-1 border border-slate-400 hover:border-[#0f172a] font-black">
              📱 Print QR Sticker
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field("Name", "name", "asset-name-input")}
            {field("Location", "location", "asset-location-input", "e.g. Basement laundry room")}
            {field("Make", "make", "asset-make-input", "e.g. Speed Queen")}
            {field("Model", "model", "asset-model-input", "e.g. TR7003WN")}
            {field("Serial", "serial", "asset-serial-input")}
            {field("Install Date", "install_date", "asset-install-input", "YYYY-MM-DD")}
            {field("Vendor", "vendor", "asset-vendor-input", "e.g. Pay Range")}
            <div>
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Status</label>
              <select value={draft.status || "operational"} onChange={e => setDraft({ ...draft, status: e.target.value })}
                data-testid="asset-status-select"
                className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1 bg-white">
                <option value="operational">Operational</option>
                <option value="broken">Broken</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Notes</label>
            <textarea value={draft.notes || ""} onChange={e => setDraft({ ...draft, notes: e.target.value })}
              data-testid="asset-notes-input" rows={3}
              className="w-full border-2 border-[#0f172a] p-2 text-sm mt-1"/>
          </div>

          {/* WARRANTY BLOCK ---------------------------------------------------- */}
          <div className="border-2 border-fuchsia-500 bg-fuchsia-50 p-3" data-testid="asset-warranty-block">
            <div className="flex items-center justify-between">
              <div className="font-display font-black text-xs uppercase tracking-widest text-fuchsia-800">
                🛡️ Warranty
              </div>
              {asset.warranty_status === "active" && (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white"
                  data-testid="asset-warranty-badge-active">
                  Active · {asset.warranty_days_left}d left
                </span>
              )}
              {asset.warranty_status === "expiring_soon" && (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white"
                  data-testid="asset-warranty-badge-expiring">
                  Expiring · {asset.warranty_days_left}d left
                </span>
              )}
              {asset.warranty_status === "expired" && (
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-600 text-white"
                  data-testid="asset-warranty-badge-expired">
                  Expired
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field("Warranty Start", "warranty_start", "asset-warranty-start-input", "YYYY-MM-DD")}
              {field("Warranty End", "warranty_end", "asset-warranty-end-input", "YYYY-MM-DD")}
              {field("Provider", "warranty_provider", "asset-warranty-provider-input", "e.g. Home Depot Pro")}
              {field("Policy #", "warranty_policy_number", "asset-warranty-policy-input")}
              <div>
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-500">Coverage Years</label>
                <input type="number" min="0" max="20" value={draft.warranty_years ?? ""}
                  onChange={e => setDraft({ ...draft, warranty_years: e.target.value })}
                  data-testid="asset-warranty-years-input" placeholder="e.g. 5"
                  className="w-full h-9 border-2 border-[#0f172a] px-2 text-sm mt-1"/>
              </div>
            </div>
            <div className="mt-2 text-[10px] font-medium text-fuchsia-800">
              Warranty end drives the "🛡️ Under Warranty · Claim Replacement" flag on the assets list — set it here and predictive maintenance will tell workers to file a claim instead of billing a repair.
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} disabled={saving}
              className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest">
              Cancel
            </button>
            <button onClick={save} disabled={saving} data-testid="asset-save-btn"
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-1">
              <Check weight="bold" size={14}/> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-[130] bg-black/70 flex items-center justify-center p-4" data-testid="asset-photo-delete-confirm">
          <div className="bg-white p-5 max-w-sm w-full">
            <div className="font-display font-black text-lg uppercase tracking-tight">Delete this photo?</div>
            <div className="text-xs text-slate-600 mt-1">The photo is removed from this asset. This can't be undone from the UI.</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setPendingDelete(null)} data-testid="asset-photo-delete-cancel"
                className="flex-1 h-10 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest">Cancel</button>
              <button onClick={confirmDelete} data-testid="asset-photo-delete-confirm-btn"
                className="flex-1 h-10 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-[10px] tracking-widest">Delete</button>
            </div>
          </div>
        </div>
      )}
      {photoIdx !== null && photos[photoIdx] && (
        <div className="fixed inset-0 z-[120] bg-black flex items-center justify-center" onClick={() => setPhotoIdx(null)}>
          <button onClick={() => setPhotoIdx(null)} className="absolute top-4 right-4 text-white p-2 z-10" data-testid="photo-close"><X size={28}/></button>
          {photoIdx > 0 && <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(photoIdx - 1); }}
            className="absolute left-4 text-white text-4xl font-black z-10">‹</button>}
          {photoIdx < photos.length - 1 && <button onClick={(e) => { e.stopPropagation(); setPhotoIdx(photoIdx + 1); }}
            className="absolute right-4 text-white text-4xl font-black z-10">›</button>}
          <img src={streamUrl(photos[photoIdx])} alt="" onClick={e => e.stopPropagation()}
            data-testid={`asset-fullscreen-photo-${photoIdx}`}
            className="max-w-[95%] max-h-[95%] object-contain"/>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs font-black tracking-widest">
            {photoIdx + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
